import React, { useEffect, useState } from "react";
import { Box, Grid, IconButton, Tooltip } from "@mui/material";
import { useToast } from "../../services/utils";
import FlexBetween from "../../components/FlexBetween";
import DataTable from "../../components/DataGrids/DataGrid";
import { GridColDef } from "@mui/x-data-grid";

import { useNavigate, useSearchParams } from "react-router-dom";
import ButtonComponent from "../../components/Button.Component";
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from "@mui/x-data-grid-pro";
import { deleteDomain, reactivateDomain } from "../../services/domainsService";
import AddSharpIcon from "@mui/icons-material/AddSharp";
import { useAuth } from "../../contexts/AuthContext";
import {
  IconAudit,
  IconCheckCircleIconGreenSvg,
  IconCrossCircleIconSvg,
  IconDeleteBlueSvg,
  IconEditBase,
  IconExportIconBlue,
  IconEyeBase,
  IconReactivateSvg,
  IconReportsSvg,
  IconViewResource,
  IconViewRuleBase,
} from "../../common/utils/icons";
import {
  downloadDomainExportFile,
  getCurrentDateAndLast7Days,
  getFormattedDateTime,
} from "../../services/utils";
import useFetchPaginatedDomain from "../../hooks/useFetchPaginatedDomain";
import { paginatedResponseFormat } from "../../services/constants";
import Loader from "../../components/Molecules/Loader/Loader";
import DropdownMenu from "../../components/Molecules/DropdownMenu/DropdownMenu";
import LimitChractersWithTooltip from "../../common/utils/LimitChractersWithTooltip";
import ConfirmDailog from "../../components/Dialogs/ConfirmDailog";
import { useRowsPerPage } from "../../hooks/useRowsPerPage";
import SearchFilterBar from "../../components/Molecules/FilterSearch/SearchFilterBar";
import { getConfig } from "../../config/runtimeConfig";
import "./Domain.scss";

// Centralized Filter Configuration for Domain List
const DOMAIN_FILTER_CONFIG = {
  // Default filter state - single source of truth
  DEFAULT_STATE: {
    domain_name: "",
    status_filter: "Active",
  },

  // Fields to persist in URL
  get URL_FIELDS() {
    return Object.keys(this.DEFAULT_STATE);
  },

  // Field type mappings for URL serialization/deserialization
  FIELD_TYPES: {
    ID_FIELDS: [],
    STRING_FIELDS: ["domain_name", "status_filter"],
    DATE_FIELDS: [],
  },
} as const;

const DomainTest: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();
  const { hasPermission } = useAuth();

  // Permission checks for domain actions
  const canViewDomain = hasPermission("domain", "view");
  const canAddDomain = hasPermission("domain", "add");
  const canEditDomain = hasPermission("domain", "edit");
  const canDeleteDomain = hasPermission("domain", "delete");
  const canViewResource = hasPermission("resource", "view");
  const canViewRule = hasPermission("rule", "view");
  const canValidationResult = hasPermission("validation_result", "view");
  const canComparisonResult = hasPermission("rule_result", "view");

  // states
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [domainsData, setDomainData] = useState<any>(paginatedResponseFormat);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
  const [domainToDelete, setDomainToDelete] = useState<any>(null);

  //for pagination default values and states
  const defaultPage = getConfig("REACT_APP_DEFAULT_PAGE", "1");
  const { rowsPerPage } = useRowsPerPage();
  const [page, setPage] = useState<number>(
    defaultPage ? parseInt(defaultPage) : 1
  );
  const [pSize, setPSize] = useState<number>(rowsPerPage);

  // SearchFilterBar state - initialize from URL parameters
  const [searchFilterData, setSearchFilterData] = useState<any>(() => {
    const initialState: any = {
      ...DOMAIN_FILTER_CONFIG.DEFAULT_STATE,
    };

    // Restore filters from URL parameters
    DOMAIN_FILTER_CONFIG.URL_FIELDS.forEach((field: string) => {
      const urlValue = searchParams.get(field);
      if (urlValue) {
        initialState[field] = urlValue;
      }
    });

    return initialState;
  });

  // Extract filters from searchFilterData
  const effectiveStatusFilter = searchFilterData?.status_filter || "";
  const effectiveDomainName = searchFilterData?.domain_name || "";

  // hooks
  const [fetchedDomainsData, refetchDomains] = useFetchPaginatedDomain({
    setIsLoading,
    page,
    pSize,
    searchTerm: effectiveDomainName, // Use domain_name from searchFilterData
    statusFilter: effectiveStatusFilter,
  }) as [any, () => Promise<void>];

  // Sync filters to URL parameters (state → URL)
  useEffect(() => {
    const newParams = new URLSearchParams();

    DOMAIN_FILTER_CONFIG.URL_FIELDS.forEach((field) => {
      const value = searchFilterData[field];
      if (value !== null && value !== undefined && value !== "") {
        // Don't add default values to URL to keep it clean
        if (field === "status_filter" && value === "Active") {
          return; // Skip default status filter
        }
        newParams.set(field, String(value));
      }
    });

    // Only update URL if params have changed
    const currentParamsString = searchParams.toString();
    const newParamsString = newParams.toString();
    if (currentParamsString !== newParamsString) {
      setSearchParams(newParams, { replace: true });
    }
  }, [searchFilterData, searchParams, setSearchParams]);

  // Update filter state when URL changes (URL → state for back/forward navigation)
  useEffect(() => {
    const urlFilterState: any = {
      ...DOMAIN_FILTER_CONFIG.DEFAULT_STATE,
    };

    // Extract filter parameters from URL
    DOMAIN_FILTER_CONFIG.URL_FIELDS.forEach((field: string) => {
      const urlValue = searchParams.get(field);
      if (urlValue) {
        urlFilterState[field] = urlValue;
      }
    });

    // Check if URL filter state differs from current filter state
    const hasFilterChanges = Object.keys(urlFilterState).some((key) => {
      const urlValue = urlFilterState[key];
      const currentValue = searchFilterData[key];

      // Handle null/undefined/empty string comparisons
      const normalizedUrlValue =
        urlValue === null || urlValue === undefined || urlValue === ""
          ? ""
          : urlValue;
      const normalizedCurrentValue =
        currentValue === null ||
        currentValue === undefined ||
        currentValue === ""
          ? ""
          : currentValue;

      return normalizedUrlValue !== normalizedCurrentValue;
    });

    // Only update state if there are actual changes to prevent infinite loops
    if (hasFilterChanges) {
      setSearchFilterData(urlFilterState);
    }
  }, [searchParams]); // Only depend on searchParams to detect URL changes

  // Reset page to 1 when search filter changes
  useEffect(() => {
    // Reset page when status filter or domain name search changes
    if (
      searchFilterData?.status_filter !== undefined ||
      searchFilterData?.domain_name !== undefined
    ) {
      setPage(1);
    }
  }, [searchFilterData]);

  const [isDownloadLoading, setIsDownloadLoading] = useState<boolean>(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const handelClickEvent = () => {
    navigate("/domain/add");
  };
  // Handle delete domain
  const handleDeleteDomain = (domain: any) => {
    setDomainToDelete(domain);
    setDeleteConfirmOpen(true);
  };

  // Confirm delete action
  const confirmDelete = async () => {
    if (!domainToDelete) return;

    try {
      setDeleteLoading(true);
      const result = await deleteDomain(domainToDelete.id);

      if (result && result.status === 200) {
        showToast(
          `${domainToDelete.domain_name} deleted successfully`,
          "success"
        );
        setDeleteConfirmOpen(false);
        setDomainToDelete(null);
        // Update the domains list to remove the deleted domain
        setDomainData((prev: any) => ({
          ...prev,
          items: prev?.items?.filter(
            (prevItem: any) => prevItem.id !== domainToDelete.id
          ),
        }));
      }
    } catch (error: any) {
      showToast(error?.message || "Failed to delete domain", "error");
    } finally {
      setDeleteLoading(false);
      setDeleteConfirmOpen(false);
    }
  };

  // Handle reactivate domain
  const handleReactivate = async (id: number, domainName: string) => {
    setIsLoading(true);
    try {
      await reactivateDomain(id);
      showToast(`Domain "${domainName}" reactivated successfully`, "success");

      // Optimistic UI update - immediately update the is_active flag in local state
      setDomainData((prev: any) => ({
        ...prev,
        items: prev?.items?.map((item: any) =>
          item.id === id ? { ...item, is_active: true } : item
        ),
      }));

      // Refetch the list to update the UI with fresh data from database
      refetchDomains();
    } catch (error) {
      showToast("Error reactivating domain", "error");
    } finally {
      setIsLoading(false);
    }
  };
  const columns: GridColDef[] = [
    {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      width: 1,
      minWidth: 20,
      flex: 0,
      renderCell: (params: any) => <></>,
      cellClassName: "no-padding-cell",
      headerClassName: "no-padding-cell",
    },
    {
      field: "id",
      headerName: "ID",
      width: 100,
      renderCell: (params: any) => {
        const handleClickDomain = () => {
          navigate(`/resource/${params.id}`);
        };
        return (
          <span style={{ cursor: "pointer" }} onClick={handleClickDomain}>
            {params.id}
          </span>
        );
      },
    },
    {
      field: "domain_name",
      headerName: "Domain",
      width: 100,
      flex: 1,
      renderCell: (params: any) => {
        const handleClickDomain = () => {
          navigate(`/resource/${params.id}`);
        };
        return (
          <LimitChractersWithTooltip
            value={params?.value}
            onClick={handleClickDomain}
          />
        );
      },
    },
    {
      field: "domain_desc",
      headerName: "Description",
      flex: 1,
      renderCell: (params: any) => {
        const handleClickDomain = () => {
          navigate(`/resource/${params.id}`);
        };
        return (
          <LimitChractersWithTooltip
            value={params?.value}
            onClick={handleClickDomain}
          />
        );
      },
    },
    {
      field: "modified_by_user",
      headerName: "Modified By",
      flex: 1,
      renderCell: (params: any) => {
        const handleClickDomain = () => {
          navigate(`/resource/${params.id}`);
        };
        return (
          <LimitChractersWithTooltip
            value={params?.value?.username}
            onClick={handleClickDomain}
          />
        );
      },
    },
    {
      field: "updated_on",
      headerName: "Last Modified",
      flex: 1,
      renderCell: (params: any) => {
        const handleClickDomain = () => {
          navigate(`/resource/${params.id}`);
        };

        return (
          <span style={{ cursor: "pointer" }} onClick={handleClickDomain}>
            {getFormattedDateTime(params.value)}
            {/* {params.value} */}
          </span>
        );
      },
    },
    {
      field: "is_active",
      headerName: "Status",
      width: 100,
      align: "center",
      headerAlign: "center",
      renderCell: (params: any) => {
        return params.value ? (
          <Tooltip title="Active" placement="top" arrow>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconCheckCircleIconGreenSvg width={21} height={21} />
            </Box>
          </Tooltip>
        ) : (
          <Tooltip title="Inactive" placement="top" arrow>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconCrossCircleIconSvg width={21} height={21} />
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "action",
      headerName: "Actions",
      align: "center",
      headerAlign: "center",
      width: 200, // BUG-39: Increased from 130 to 200 for better spacing of action buttons
      renderCell: (params: any) => {
        const isActive = params.row.is_active === true;
        const handleDelete = () => {
          handleDeleteDomain(params.row);
        };
        const { last7DaysDate } = getCurrentDateAndLast7Days();

        return (
          <>
            {/* For active records, show all action buttons */}
            {isActive && (
              <>
                {/* View button */}
                {canViewDomain && (
                  <Tooltip title="View Domain" placement="top" arrow>
                    <IconButton
                      className="datagrid-action-btn"
                      color="inherit"
                      onClick={() => navigate(`/domain/view/${params.row.id}`)}
                    >
                      <IconEyeBase />
                    </IconButton>
                  </Tooltip>
                )}

                {/* Edit button */}
                {canEditDomain && (
                  <Tooltip title="Edit Domain" placement="top" arrow>
                    <IconButton
                      className="datagrid-action-btn"
                      color="inherit"
                      onClick={() => navigate(`/domain/edit/${params.row.id}`)}
                    >
                      <IconEditBase />
                    </IconButton>
                  </Tooltip>
                )}

                {/* Dropdown menu */}
                <DropdownMenu
                  dropdownMenuItems={[
                    // Audit Domain - requires view permission
                    ...(canViewDomain
                      ? [
                          <IconButton
                            key="audit-domain"
                            className="datagrid-action-btn"
                            color="inherit"
                            onClick={() =>
                              navigate(`/domain/auditDomain/${params.row.id}`)
                            }
                          >
                            <IconAudit /> Audit Domain
                          </IconButton>,
                        ]
                      : []),
                    // View Linked Resources - requires view permission
                    ...(canViewResource
                      ? [
                          <IconButton
                            key="view-linked-resources"
                            className="datagrid-action-btn"
                            color="inherit"
                            onClick={() => {
                              navigate(`/resource/${params.id}`);
                            }}
                          >
                            <IconViewResource />
                            View Linked Resources
                          </IconButton>,
                        ]
                      : []),
                    // View Linked Rules - requires view permission
                    ...(canViewRule
                      ? [
                          <IconButton
                            key="view-linked-rules"
                            className="datagrid-action-btn"
                            color="inherit"
                            onClick={() => {
                              navigate(`/rules-list/${params.id}`);
                            }}
                          >
                            <IconViewRuleBase />
                            View Linked Rules
                          </IconButton>,
                        ]
                      : []),
                    // Recent Comparisons - requires view permission
                    ...(canComparisonResult
                      ? [
                          <IconButton
                            key="recent-comparisons"
                            onClick={() => {
                              navigate(
                                `/rules-execution-history?domainId=${params.row.id}&fromDate=${last7DaysDate}`
                              );
                            }}
                            size="small"
                            className="datagrid-action-btn"
                          >
                            <IconReportsSvg />
                            Recent Comparisons
                          </IconButton>,
                        ]
                      : []),
                    // Recent Validations - requires view permission
                    ...(canValidationResult
                      ? [
                          <IconButton
                            key="recent-validations"
                            onClick={() => {
                              navigate(
                                `/validation-execution-history?domainId=${params.row.id}&fromDate=${last7DaysDate}`
                              );
                            }}
                            size="small"
                            className="datagrid-action-btn"
                          >
                            <IconReportsSvg />
                            Recent Validations
                          </IconButton>,
                        ]
                      : []),
                    // Export - requires view permission
                    ...(canViewDomain
                      ? [
                          <IconButton
                            key="export"
                            className="datagrid-action-btn"
                            color="inherit"
                            onClick={() =>
                              downloadDomainExportFile(
                                params.row.id,
                                setIsDownloadLoading
                              )
                            }
                          >
                            <IconExportIconBlue />
                            Export
                          </IconButton>,
                        ]
                      : []),
                    // Delete Domain - requires delete permission
                    ...(canDeleteDomain
                      ? [
                          <IconButton
                            key="delete-domain"
                            className="datagrid-action-btn"
                            color="inherit"
                            onClick={() => handleDelete()}
                          >
                            <IconDeleteBlueSvg />
                            Delete Domain
                          </IconButton>,
                        ]
                      : []),
                  ]}
                />
              </>
            )}

            {/* For inactive records, show ONLY Reactivate button */}
            {!isActive && canEditDomain && (
              <Tooltip title="Reactivate Domain" placement="top" arrow>
                <IconButton
                  className="datagrid-action-btn"
                  color="inherit"
                  onClick={() =>
                    handleReactivate(params.row.id, params.row.domain_name)
                  }
                >
                  <IconReactivateSvg />
                </IconButton>
              </Tooltip>
            )}
          </>
        );
      },
    },
  ];
  // for update domains list data
  useEffect(() => {
    if (fetchedDomainsData) {
      // setFileData(fetchedDomainsData);
      setDomainData(fetchedDomainsData);
    }
  }, [fetchedDomainsData]);

  return (
    <Box className="domain-page">
      {(isLoading || isDownloadLoading) && (
        <Loader isLoading={isLoading || isDownloadLoading} />
      )}
      {deleteLoading && <Loader isLoading={deleteLoading} zIndex={1400} />}
      <ConfirmDailog
        isDailogOpen={deleteConfirmOpen}
        handleCancelAction={() => setDeleteConfirmOpen(false)}
        handleConfirmAction={confirmDelete}
        dailogTitle="Delete Domain"
        dailogDescription={`Are you sure you want to delete domain "${domainToDelete?.domain_name}"?`}
      />

      <Box className="text-box-card list-page-card compact-text-box-card">
        <Grid container rowSpacing={2.5} columnSpacing={2}>
          <Grid item xs>
            <SearchFilterBar
              largeWidth={true}
              setSearchFilterData={setSearchFilterData}
              searchFilterData={searchFilterData}
              requiredFields={["domain_name", "status_filter"]}
              primaryFields={["domain_name", "status_filter"]}
              fieldRequirements={{
                defaultStatusFilter: "Active", // Reset to Active status on Clear Filter
              }}
            />
          </Grid>
          <Grid
            item
            xs="auto"
            sx={{ display: "flex", alignItems: "flex-start" }}
          >
            {canAddDomain && (
              <ButtonComponent
                handelClickEvent={handelClickEvent}
                className="btn-orange"
              >
                <>
                  <AddSharpIcon sx={{ marginRight: "4px" }} /> Domain
                </>
              </ButtonComponent>
            )}
          </Grid>
        </Grid>
      </Box>
      {/* import  button component */}

      {/* import data table for resource list  */}
      <FlexBetween>
        <DataTable
          dataColumns={columns || []}
          dataRows={domainsData?.items || []}
          loading={isLoading}
          dataListTitle={"Domain List"}
          className="dataTable no-radius hide-progress-icon"
          paginationMode="server"
          rowCount={domainsData?.total || 0}
          paginationModel={{
            page: page - 1,
            pageSize: pSize,
          }}
          onPaginationModelChange={(params: any) => {
            if (params.pageSize !== pSize || params.page !== page - 1) {
              setPage(params.page + 1);
              setPSize(params.pageSize);
            }
          }}
          fitViewport
          singlePageMaxHeightDiff={358}
        />
      </FlexBetween>
    </Box>
  );
};

export default DomainTest;
